import React, {type Dispatch,
    type SetStateAction,
    useCallback,
} from 'react';


import {ImageSelect} from 'src/components/image-select';
import {FormInput} from 'src/components/input';
import {RequiredMark} from 'src/components/required-mark';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {CustomValidatedControl, ValidatedControl} from 'src/contexts/validation-context';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights';

import {Group} from '@pb/kit-component-group';

import {MICROSITE_LOGO_CONTROL, MICROSITE_NAME_CONTROL} from './constants';
import {MicroSiteBaseInfoSkeleton} from './microsite-base-info.skeleton';

import type {AccessRight} from 'src/hooks/access-rights/constants';
import type {MicroSiteBaseInfo} from 'src/modules/profile/account/tabs/microsites/typedef';
import type {Error} from 'src/openapi/admin/models/Error';

type FileWithPreview = File & {preview?: string};

const getFilePreview = (file?: File | null): string | null => {
    if (file && typeof (file as FileWithPreview).preview === 'string') {
        return (file as FileWithPreview).preview ?? null;
    }

    return null;
};

type Props = {
    isLoading: boolean,
    loadingError: Error | null,
    microSiteBaseInfo: MicroSiteBaseInfo,
    setMicroSiteBaseInfo: Dispatch<SetStateAction<MicroSiteBaseInfo>>,
    trackChange: (parameter: string) => void,
    readonly?: boolean,
};

export const MicroSiteBaseInfoSection = (props: Props) => {
    const {
        isLoading,
        loadingError,
        microSiteBaseInfo,
        setMicroSiteBaseInfo,
        trackChange,
        readonly,
    } = props;

    const changeMicroSiteName = useCallback((micrositeName: string) => {
        trackChange('Microsite Name');
        setMicroSiteBaseInfo(prevState => ({
            ...prevState,
            micrositeName,
        }));
    }, [setMicroSiteBaseInfo, trackChange]);

    const clearMicrositeLogo = useCallback(() => {
        trackChange('Microsite Logo');
        setMicroSiteBaseInfo(prevState => ({
            ...prevState,
            logoFile: null,
            logo: '',
        }));
    }, [setMicroSiteBaseInfo, trackChange]);

    const changeMicrositeLogo = useCallback((logoFile: File) => {
        trackChange('Microsite Logo');
        setMicroSiteBaseInfo(prevState => ({
            ...prevState,
            logoFile,
        }));
    }, [setMicroSiteBaseInfo, trackChange]);

    return (
        <Skeleton shown={isLoading} view={<MicroSiteBaseInfoSkeleton/>}>
            <ServerResponseHandler
                smallView={true}
                sectionName="Microsite Base Configuration"
                accessRight={ACCESS_RIGHTS.MICROSITES_SCREEN_VIEW as AccessRight}
                serverError={loadingError}>
                <Group direction="v" alignItems="start">
                    <CustomValidatedControl name={MICROSITE_NAME_CONTROL}>
                        {error => (
                            <FormInput
                                label={<RequiredMark label="Microsite Name"/>}
                                value={microSiteBaseInfo.micrositeName}
                                width={300}
                                readOnly={readonly}
                                placeholder="Site name"
                                onBlur={changeMicroSiteName}
                                valid={error ? false : undefined}
                                errorDescription={error}
                            />
                        )}
                    </CustomValidatedControl>
                    <ValidatedControl name={MICROSITE_LOGO_CONTROL}>
                        {() => (
                            <ImageSelect
                                height={294}
                                width={750}
                                readonly={readonly}
                                label={<RequiredMark label="Microsite logo"/>}
                                description={(
                                    <p>
                                        Upload a logo image to add to the account.
                                        <b>Please note:</b> Recommended image size: 494px x 151px
                                    </p>
                                )}
                                img={getFilePreview(microSiteBaseInfo.logoFile) ?? microSiteBaseInfo.logo}
                                maxSize={10}
                                alt="Microsite logo"
                                onDrop={changeMicrositeLogo}
                                onRemove={clearMicrositeLogo}
                            />
                        )}
                    </ValidatedControl>
                </Group>
            </ServerResponseHandler>
        </Skeleton>
    );
};
